import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { Socket } from "socket.io-client";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import type { useChannelLocks } from "../../../hooks/useChannelLocks";
import type { Channel } from "../chatTypes";
import type { useChannelFolders } from "./useChannelFolders";
import type { useChannelReadState } from "./useChannelReadState";
import type { useFormData } from "./useFormData";
import type { useKanbanData } from "./useKanbanData";
import type { useLastChannelBySpace } from "./useLastChannelBySpace";
import type { usePresence } from "./usePresence";
import type { useSpaces } from "./useSpaces";
import type { useUnread } from "./useUnread";
import type { useVoiceCall } from "./useVoiceCall";

const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);
const normalizeChannelId = (chan: string) => (chan.includes(':') ? chan.split(':').slice(-1)[0] : chan);

export type ChannelNavigation = {
  switchVoid: (id: string) => void;
  switchChannel: (id: string) => void;
  openChannelFromFqid: (fqid: string, fallbackVoidId?: string) => void;
};

/**
 * Space and channel navigation: switching space (including DMs), switching channel (with the
 * voice-call, access and unlock checks), and opening a channel from a fully-qualified id.
 *
 * ChatLayout hands the hooks that run before this one (locks, DMs, socket handlers) forwarding
 * wrappers, because those hooks receive these functions while this one reads their results.
 */
export function useChannelNavigation({
  socket,
  currentVoidId,
  currentVoidIdRef,
  channelPaneView,
  setChannelPaneView,
  setCurrentVoidId,
  setCurrentChannelId,
  onDismissProfileRoute,
  onDismissSettingsRoute,
  voids,
  channels,
  isDmSpace,
  canAccessChannel,
  setChannelFolders,
  setUnread,
  setNotified,
  setTypers,
  setRoomUserIds,
  markChannelRead,
  pendingChannelSwitchRef,
  maybePromptChannelUnlock,
  liveKitRoomRef,
  voiceActiveChannelId,
  voiceActiveSpaceId,
  setVoiceSwitchPrompt,
  loadKanbanIfNeeded,
  loadFormIfNeeded,
  getLastChannelBySpace,
  rememberLastChannel,
  notifyNativeChannelViewed,
}: {
  socket: Socket;
  currentVoidId: string;
  currentVoidIdRef: MutableRefObject<string>;
  channelPaneView: "spaces" | "dms";
  setChannelPaneView: Dispatch<SetStateAction<"spaces" | "dms">>;
  setCurrentVoidId: Dispatch<SetStateAction<string>>;
  setCurrentChannelId: Dispatch<SetStateAction<string>>;
  onDismissProfileRoute?: () => void;
  onDismissSettingsRoute?: () => void;
  voids: ReturnType<typeof useSpaces>["voids"];
  channels: Channel[];
  isDmSpace: boolean;
  canAccessChannel: ReturnType<typeof useSpaces>["canAccessChannel"];
  setChannelFolders: ReturnType<typeof useChannelFolders>["setChannelFolders"];
  setUnread: ReturnType<typeof useUnread>["setUnread"];
  setNotified: ReturnType<typeof useUnread>["setNotified"];
  setTypers: ReturnType<typeof usePresence>["setTypers"];
  setRoomUserIds: ReturnType<typeof usePresence>["setRoomUserIds"];
  markChannelRead: ReturnType<typeof useChannelReadState>["markChannelRead"];
  pendingChannelSwitchRef: ReturnType<typeof useChannelLocks>["pendingChannelSwitchRef"];
  maybePromptChannelUnlock: ReturnType<typeof useChannelLocks>["maybePromptChannelUnlock"];
  liveKitRoomRef: ReturnType<typeof useVoiceCall>["liveKitRoomRef"];
  voiceActiveChannelId: ReturnType<typeof useVoiceCall>["voiceActiveChannelId"];
  voiceActiveSpaceId: ReturnType<typeof useVoiceCall>["voiceActiveSpaceId"];
  setVoiceSwitchPrompt: ReturnType<typeof useVoiceCall>["setVoiceSwitchPrompt"];
  loadKanbanIfNeeded: ReturnType<typeof useKanbanData>["loadKanbanIfNeeded"];
  loadFormIfNeeded: ReturnType<typeof useFormData>["loadFormIfNeeded"];
  getLastChannelBySpace: ReturnType<typeof useLastChannelBySpace>["getLastChannelBySpace"];
  rememberLastChannel: ReturnType<typeof useLastChannelBySpace>["rememberLastChannel"];
  notifyNativeChannelViewed: (spaceId: string, channelId: string) => void;
}): ChannelNavigation {
  function switchVoid(id: string) {
    if (id === currentVoidId) {
      // If the user clicks their current space while the DMs pane is open, bring them back to channels.
      if (channelPaneView === 'dms') setChannelPaneView('spaces');
      return;
    }
    onDismissProfileRoute?.();
    onDismissSettingsRoute?.();
    currentVoidIdRef.current = id;
    setCurrentVoidId(id);
    setChannelPaneView(String(id).startsWith('dm_') ? 'dms' : 'spaces');
    socket.emit("void:switch", { voidId: id });
    socket.emit("channel:list", { voidId: id });
    // Members (and the DM peer derived from them) are loaded by the
    // currentVoidId effect below, so every navigation path stays in sync.
    // load channel folders for sidebar grouping
    (async () => {
      try {
        const tok = getAuthToken() || '';
        const res = await api.getAuth(`/spaces/${encodeURIComponent(id)}/folders`, tok);
        setChannelFolders(res.folders || []);
      } catch { setChannelFolders([]); }
    })();
    if (String(id).startsWith('dm_')) {
      // Clear unread + notification for this DM when opened via icon, and advance
      // the server read watermark so it doesn't resurface on the next hydration.
      const cid = `${id}:chat`;
      setUnread(prev => ({ ...prev, [cid]: 0 }));
      setNotified(prev => { const n = { ...prev }; delete n[cid]; return n; });
      markChannelRead(id, 'chat');
      const short = 'chat';
      pendingChannelSwitchRef.current = { voidId: id, channelId: short };
      setCurrentChannelId(short);
      socket.emit("channel:switch", { voidId: id, channelId: `${id}:chat` });
      notifyNativeChannelViewed(id, short);
    } else {
      const v = voids.find(vv => vv.id === id) as any;
      const home = v?.homeChannelId as string | undefined;
      const remembered = getLastChannelBySpace()[id];
      const short = normalizeChannelId(remembered || home || 'general');
      const fqId = fq(id, short);
      pendingChannelSwitchRef.current = { voidId: id, channelId: short };
      // Store the fully-qualified id: channels in state use fq ids, so a short id
      // here fails every `c.id === currentChannelId` check (sidebar highlight,
      // currentChannel lookup, the onChannelList reconciliation) and bounces us onto
      // the first channel instead of the remembered one.
      setCurrentChannelId(fqId);
      socket.emit("channel:switch", { voidId: id, channelId: fqId });
      notifyNativeChannelViewed(id, short);
    }
    setTypers({}); setRoomUserIds([]);
  }
  function switchChannel(id: string) {
    const spaceId = currentVoidIdRef.current || currentVoidId;
    if (!spaceId) return;
    const channelMeta = channels.find(c => c.id === id);
    // Already on a call: navigating into a DIFFERENT voice channel must prompt to
    // disconnect first (one call at a time). Ref, not state — leaveVoice() clears the
    // ref synchronously so confirmVoiceSwitch's own navigation isn't re-blocked.
    if (channelMeta?.type === 'voice' && liveKitRoomRef.current) {
      const isActiveCallChannel = voiceActiveChannelId === id && voiceActiveSpaceId === spaceId;
      if (!isActiveCallChannel) {
        setVoiceSwitchPrompt({ channelId: id, spaceId });
        return;
      }
    }
    pendingChannelSwitchRef.current = { voidId: spaceId, channelId: id };
    if (!isDmSpace && channelMeta && !canAccessChannel(channelMeta)) return;
    if (maybePromptChannelUnlock(spaceId, id, channelMeta?.name)) return;
    onDismissProfileRoute?.();
    onDismissSettingsRoute?.();
    setCurrentChannelId(id);
    rememberLastChannel(spaceId, id);
    const fqid = fq(spaceId, id);
    socket.emit("channel:switch", { voidId: spaceId, channelId: fqid });
    notifyNativeChannelViewed(spaceId, id);
    setTypers({});
    // clear unread + notified once user opens, and advance the server read
    // watermark from the last loaded message so the cleared badge doesn't
    // resurface on the next unread hydration (no-op until messages are loaded).
    const cid = fq(spaceId, id);
    setUnread(prev => ({ ...prev, [cid]: 0 }));
    setNotified(prev => { const n = { ...prev }; delete n[cid]; return n; });
    markChannelRead(spaceId, id);
    const meta = channels.find(c => c.id === id);
    if (meta && meta.type === 'kanban') { loadKanbanIfNeeded(fqid); }
    if (meta && meta.type === 'form') { loadFormIfNeeded(fqid); }
  }

  function openChannelFromFqid(fqid: string, fallbackVoidId?: string) {
    if (!fqid) return;
    const parts = String(fqid).split(':');
    let targetVoid = '';
    let targetChannel = '';
    if (parts.length > 1) {
      targetVoid = parts[0];
      targetChannel = parts.slice(1).join(':');
    } else {
      targetVoid = fallbackVoidId || currentVoidIdRef.current || currentVoidId || '';
      targetChannel = parts[0];
    }
    if (!targetVoid || !targetChannel) return;
    if (targetVoid !== currentVoidIdRef.current) {
      switchVoid(targetVoid);
    }
    pendingChannelSwitchRef.current = { voidId: targetVoid, channelId: targetChannel };
    switchChannel(targetChannel);
  }

  return { switchVoid, switchChannel, openChannelFromFqid };
}
